import { useCallback, useEffect, useRef, useState } from "react";
import { makeGet } from "@culturehq/client";
import { v4 as uuid } from "uuid";
import { checkArray, errorService } from "app/utils";
import notificationsTypes from "constants/notificationsTypes";
import getDefaultSource from "components/stories/video-studio/utils/creatomate/getDefaultSource";
import sourceToSnakeCase from "components/stories/video-studio/utils/creatomate/sourceToSnakeCase";
import usePrevious from "./usePrevious";
import { useNotificationReceivedListener } from "./useListener";

const useLatestMedia = (
  story,
  profile,
  { landingPage = undefined, isTemplateBuilder = false, creatomateJson = undefined } = {}) => {
  const [state, _setState] = useState({
    reload: true,
    isFetching: false,
    mustProcessingMessageShow: true,
    media: { ...story?.media || {} },
    thirdPartyMedias: [],
    pagination: { currentPage: 1 },
    lastMediaVersion: undefined
  });
  const mediaId = story?.media?.id;
  const setState = newProps => _setState(prev => ({ ...prev, ...newProps }));
  const isMounted = useRef();

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const prevMediaId = usePrevious(mediaId);
  useEffect(() => {
    if (!mediaId || (!state.reload && mediaId === prevMediaId) || landingPage) return;

    setState({ isFetching: true, reload: false });
    makeGet(`/medias/${mediaId}`, { page: state.pagination.currentPage, isTemplateBuilder })
      .then(async ({ thirdPartyMedias, media, originalVersion, creatomateDraft, pagination }) => {
        if (!isMounted.current) return;

        const originalMediaVersion = await buildOriginalMediaVersion({
          ...(originalVersion || {}),
          thumbnail: media.thumbnail,
          burnSubtitles: media.burnSubtitles,
          subtitles: media.subtitles,
          url: originalVersion?.url || media.url
        }, media);

        const parsedThirdPartyMedias = parseAdditionalDataResponse(thirdPartyMedias, mediaId);
        const lastMediaVersion = parsedThirdPartyMedias[0];

        if (creatomateDraft) {
          const autosavedDraftVersion = parseAdditionalDataResponse(
            [{
              thirdPartyRenderId: uuid(),
              additionalData: creatomateDraft.creatomateJson,
              createdAt: creatomateDraft.updatedAt,
              draft: true,
              status: "succeeded",
              url: lastMediaVersion?.url || originalVersion?.url || media.url,
              metadata: lastMediaVersion ? { ...(lastMediaVersion.metadata || {}) } : undefined,
              creator: creatomateDraft.user,
              thumbnail: creatomateDraft.thumbnail,
              burnSubtitles: creatomateDraft.burnSubtitles,
              subtitles: creatomateDraft.subtitles
            }],
            mediaId
          );
          parsedThirdPartyMedias.unshift(autosavedDraftVersion[0]);
        } else {
          parsedThirdPartyMedias.unshift({
            ...originalMediaVersion,
            thirdPartyRenderId: uuid(),
            draft: true,
            original: undefined
          });
        }

        if (pagination.currentPage >= pagination.totalPages) {
          parsedThirdPartyMedias.push(originalMediaVersion);
        }

        _setState(prev => ({
          ...prev,
          isFetching: false,
          pagination,
          thirdPartyMedias: buildNewOrAppendHistory(prev, parsedThirdPartyMedias),
          media,
          lastMediaVersion
        }));
      }).catch(err => isMounted.current && setState({ isFetching: false }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaId, state.reload]);

  useNotificationReceivedListener(
    useCallback(
      notification => {
        const action = {
          [notificationsTypes.VIDEO_STORY_UPDATED]: () => {
            const incomingMediaId = notification.entity.media.id;
            if (incomingMediaId === mediaId) setState({ mustProcessingMessageShow: false });
          }
        }[notification.notificationType];

        if (!action) return;

        action();
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [state.thirdPartyMedias]
    )
  );

  const parseAdditionalDataResponse = (thirdPartyMedias, mediaId) => (
    checkArray(thirdPartyMedias).map(
      ({ additionalData, ...rest }) => ({
        ...rest,
        additionalData: additionalData ? (() => {
          try {
            return JSON.parse(additionalData);
          } catch (error) {
            errorService.error(`No valid additional data for media ${mediaId}`);
            return {};
          }
        })() : null
      }))
  );

  const buildNewOrAppendHistory = (prev, thirdPartyMedias) => (
    prev.pagination.currentPage > 1 // Must append them
      ? [...prev.thirdPartyMedias, ...thirdPartyMedias]
      : thirdPartyMedias
  );

  const buildOriginalMediaVersion = async (originalVersion, media) => ({
    ...originalVersion,
    original: true,
    thirdPartyRenderId: uuid(),
    additionalData: await buildInitialAdditionalData(originalVersion, media)
  });

  const buildInitialAdditionalData = async (originalVersion, media) => {
    if (originalVersion.additionalData) {
      return parseAdditionalDataResponse([originalVersion], media.id)[0].additionalData;
    }
    if (creatomateJson) return sourceToSnakeCase(creatomateJson);
    return getDefaultSource({
      story: {
        ...story,
        media: {
          ...story?.media || {},
          ...media,
          url: originalVersion.url,
          shareableUrl: originalVersion.url
        }
      },
      organization: profile.organization,
      original: true
    });
  };

  const onThirdPartyMediasReload = reset => {
    if (!reset && state.pagination.currentPage >= state.pagination.totalPages) return;
    _setState(prev => ({
      ...prev,
      reload: true,
      mustProcessingMessageShow: true,
      pagination: {
        ...prev.pagination,
        currentPage: reset ? 1 : prev.pagination.currentPage + 1
      }
    }));
  };

  const handleUpdateDraftVersion = draftVersion => {
    _setState(prev => ({
      ...prev,
      thirdPartyMedias: prev.thirdPartyMedias.map(version => {
        if (!version.draft) return version;

        return {
          ...version,
          ...draftVersion,
          thirdPartyRenderId: version.thirdPartyRenderId,
          createdAt: new Date()
        };
      })
    }));
  };

  return { ...state, onThirdPartyMediasReload, onUpdateDraftVersion: handleUpdateDraftVersion };
};

export default useLatestMedia;
